import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Router } from 'react-router';
import { isUnauthorizedClientError } from '~domain/helper/errors';
import { AppErrorBoundary } from '~frontend-common/components/core';
import { Clarity } from '~frontend-common/components/core/clarity';
import { BrowserNotSupported } from '~frontend-common/components/ui';
import { initRoutingHistory } from '~frontend-common/helper/init-routing-history';
import { isBrowserSupported } from '~frontend-common/helper/user-agent-util';
import { HistoryContext } from '~frontend-common/hooks/use-history';
import { initTranslator } from '~frontend-common/i18n';
import { TranslatorContext } from '~frontend-common/i18n/translator';
import { InvalidCredentialsError } from '~frontend-common/models/invalid-credentials-error';
import { GlobalStyles } from '~ui-kit/components/global-styles/global-styles';
import { ThemeProvider } from '~ui-kit/theme';
import { AppContainer } from '~web-portal/components/app-container';
import { HelpScoutBeacon } from '~web-portal/components/help-scout-beacon';
import { FrontendWebPortalConfig } from '~web-portal/config';
import { useAuthStore } from '~web-portal/stores';
import { english } from './i18n/english';
import { french } from './i18n/french';
import { Images } from './theme/images';
import { Links } from './variables/links';
import { Paths } from './variables/paths';

const { history } = initRoutingHistory();

const globalErrorHandler = (error: unknown) => {
  if (error instanceof InvalidCredentialsError || isUnauthorizedClientError(error as Error)) {
    useAuthStore.getState().clearAuth();

    if (!history.location.pathname.startsWith(Paths.login.path)) {
      history.replace(Paths.login.path);
    }
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    },
    mutations: {
      cacheTime: 0,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      globalErrorHandler(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      globalErrorHandler(error);
    },
  }),
});

export function App() {
  const [translator] = useState(initTranslator({ en: english, fr: french }));

  let errorContent: JSX.Element;

  // If browser is not supported render browser not supported dialog
  if (!isBrowserSupported()) {
    errorContent = (
      <BrowserNotSupported translator={translator} homeButtonHref={Links.webPage} srcImg={Images.header.notSupported} />
    );
  }

  const clarityIdentifier = () => {
    const authState = useAuthStore.getState();
    if (authState.isLoggedIn()) {
      // If user is logged in, use userId
      return authState.userId;
    }

    // If user is not logged in, use email from otp flow, if available
    return authState.otp?.email;
  };

  return (
    <Router history={history}>
      <HistoryContext.Provider value={history}>
        <TranslatorContext.Provider value={translator}>
          <ThemeProvider>
            <Clarity projectId={FrontendWebPortalConfig.clarityProjectId} identifier={clarityIdentifier} />
            <HelpScoutBeacon />

            <QueryClientProvider client={queryClient}>
              <GlobalStyles />
              <AppErrorBoundary
                defaultFallbackProps={{
                  imageSrc: Images,
                  redirectionPath: Paths.start,
                }}
                extraTags={{
                  userId: useAuthStore.getState()?.userId || 'n/a',
                }}
              >
                {errorContent || <AppContainer />}
              </AppErrorBoundary>
            </QueryClientProvider>
          </ThemeProvider>
        </TranslatorContext.Provider>
      </HistoryContext.Provider>
    </Router>
  );
}
